import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs, where, doc, getDoc  } from '../firebaseConfig';
import { Line } from 'react-chartjs-2';
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import Divider from "@mui/material/Divider";
import TimelineIcon from '@mui/icons-material/Timeline';
import MDTypography from "components/MDTypography";
import { MenuItem, Select } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

const AvgHoldingTimeChart = ({ userID, tradeAccountID = 'default' }) => {
  const [trades, setTrades] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [avgHoldingTimes, setAvgHoldingTimes] = useState({});
  const [accountName, setAccountName] = useState(tradeAccountID); // Default to ID
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

    // Fetch account name on mount
    useEffect(() => {
      const fetchAccountName = async () => {
        if (tradeAccountID === 'default') {
          setAccountName('Default');
          return;
        }
        try {
          const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
          if (accountDoc.exists()) {
            setAccountName(accountDoc.data().name || tradeAccountID);
          }
        } catch (error) {
          console.error('Error fetching account name:', error);
        }
      };
      fetchAccountName();
    }, [userID, tradeAccountID]);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(
          collection(db, tradesPath),
          where('status', '==', 'Closed')
        );
        const tradeSnapshot = await getDocs(tradesQuery);
        const tradesData = tradeSnapshot.docs.map(doc => ({
          id: doc.id,
          accountID: tradeAccountID,
          ...doc.data()
        }));
        setTrades(tradesData);
      } catch (error) {
        console.error('Error fetching trades for AvgHoldingTimeChart:', error);
      }
    };

    fetchTrades();
  }, [userID, tradeAccountID]);

  useEffect(() => {
    const calculateAvgHoldingTimes = () => {
      const holdingTimes = {};

      trades.forEach(trade => {
        const entryDate = new Date(trade.date);
        const exitDate = new Date(trade.exitDate);
        if (exitDate.getFullYear() === year) {
          const holdingTimeMs = exitDate - entryDate; // in milliseconds
          const holdingTimeMinutes = holdingTimeMs / (1000 * 60); // in minutes
          const month = exitDate.getMonth() + 1;

          if (!holdingTimes[month]) {
            holdingTimes[month] = [];
          }
          holdingTimes[month].push(holdingTimeMinutes);
        }
      });

      const avgTimes = {};
      for (const month in holdingTimes) {
        const total = holdingTimes[month].reduce((acc, time) => acc + time, 0);
        avgTimes[month] = total / holdingTimes[month].length;
      }
      setAvgHoldingTimes(avgTimes);
    };

    calculateAvgHoldingTimes();
  }, [trades, year]);

  const data = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Average Holding Time (hours:minutes)',
        data: Array.from({ length: 12 }, (_, i) => avgHoldingTimes[i + 1] ? avgHoldingTimes[i + 1] : 0),
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgba(75, 192, 192, 0.2)',
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            const hours = Math.floor(value / 60);
            const mins = value % 60;
            return `${hours}h ${mins}m`;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            const minutes = context.raw;
            const hours = Math.floor(minutes / 60);
            const mins = Math.floor(minutes % 60);
            return `${hours}h ${mins}m`;
          },
        },
      },
    },
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  const tooltipContent = `
The Average Holding Time Analysis displays the average trade duration per month, measured in hours and minutes. Hover over any data point to see detailed holding time stats for that month. Use the year selector to explore historical data, helping identify timeframe patterns and optimize your trade duration strategy.`;

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <TimelineIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Monthly Average Holding Time {tradeAccountID === 'default' ? '' : `(${accountName})`}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  }
                }
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="center" mt={2}>
          <Select value={year} onChange={handleYearChange}>
            {[2024, 2025, 2026].map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </MDBox>
        <MDBox height="100%">
          <Line data={data} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

AvgHoldingTimeChart.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default AvgHoldingTimeChart;